import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/use-auth'
import { useVersion } from '../hooks/use-version'
import { useAlertSummary, rollupBadge } from '../hooks/use-alert-summary'
import type { NavBadge } from '@alpha-edge/shared'
import { useFeatures } from '../hooks/use-features'
import { useNavCollapse } from '../hooks/use-nav-collapse'
import { colors } from './ui/styles'
import {
  CENTRAL_NAV_SECTIONS,
  EDGE_NAV_SECTIONS,
  filterNavSections,
  findActiveSectionKey,
  type NavItem,
  type NavSection,
} from './nav/nav-sections'
import { NavSectionBlock } from './nav/NavSectionBlock'
import { NavQuickJump } from './nav/NavQuickJump'
import { TAB_GROUPS } from '../pages/settings/tab-groups'
import { useBranding } from '../hooks/use-branding'
import { useAccessionCount } from '../hooks/use-accession-count'
import { type UserRole, roleSatisfies } from '@alpha-edge/shared'
import medflowLogo from '../assets/img/MedFlow-Alpha-LIS.png'
import { UpgradeRequiredBanner } from './UpgradeRequiredBanner'
import { ImpersonationBanner } from './ImpersonationBanner'
import { NotificationBell } from './NotificationBell'
import { SupportRequestModal } from './SupportRequestModal'
import { ReleaseHistoryModal } from './ReleaseHistoryModal'
import { useReleaseAnnounce } from '../hooks/use-release-announce'

// Reload that bypasses the HTTP cache for the SPA shell. Without the
// cache-bust query param, nginx's default cache headers can serve the
// same stale index.html + bundle back, so the build-time version would
// stay pinned and the banner would reappear. Pair with
// Cache-Control: no-cache on index.html in nginx.conf.
function forceFreshReload(): void {
  const url = new URL(window.location.href)
  url.searchParams.set('v', Date.now().toString(36))
  window.location.href = url.toString()
}

// §7.5.2: org branding in the top-right of the layout header. Renders
// the uploaded logo when set, otherwise shows a deployment-specific
// label: "MedFlow LIS Central Management" on central, or the org
// name on edge deployments.
function LayoutHeaderBrand(): JSX.Element {
  const { user } = useAuth()
  const branding = useBranding(user?.organizationId)
  const features = useFeatures()
  if (branding.logoUrl) {
    return <img src={branding.logoUrl} alt="Organization logo" style={{ maxHeight: 32, maxWidth: 180 }} />
  }
  const label = features.central ? 'MedFlow LIS Central Management' : (branding.orgName ?? 'MedFlow LIS')
  return <span style={{ color: colors.text, fontSize: '1.125rem', fontWeight: 600 }}>{label}</span>
}

export function Layout() {
  const { user, logout } = useAuth()
  const { buildTime: buildTimeVersion, runtime: runtimeVersion } = useVersion()
  const alertSummary = useAlertSummary()
  const accessionCount = useAccessionCount()
  const features = useFeatures()
  const location = useLocation()
  // Per-item children expansion (e.g. QC, Devices) -- distinct from
  // whole-section collapse, which useNavCollapse owns.
  const [expandedItems, setExpandedItems] = useState<ReadonlySet<string>>(new Set())
  const [quickJumpQuery, setQuickJumpQuery] = useState('')
  const [supportOpen, setSupportOpen] = useState(false)
  // Release-history modal (#236): opened either on demand (click on the
  // version footer -- full history) or once per version via
  // useReleaseAnnounce (compact preview). Both paths share one modal
  // instance; whichever triggered the open decides the previewCount.
  const [releaseHistoryManualOpen, setReleaseHistoryManualOpen] = useState(false)
  const [showReleaseAnnounce, dismissReleaseAnnounce] = useReleaseAnnounce(runtimeVersion)
  const releaseHistoryOpen = releaseHistoryManualOpen || showReleaseAnnounce
  const closeReleaseHistory = () => {
    setReleaseHistoryManualOpen(false)
    dismissReleaseAnnounce()
  }

  const role = user?.role as UserRole | undefined

  const navBadges: Record<string, NavBadge> = {}
  const accBadge: NavBadge = { count: accessionCount, severity: 'info' }
  if (accBadge.count > 0) navBadges['/accessioning'] = accBadge

  const { nav } = alertSummary
  if (nav.alerts.count > 0) {
    navBadges['/fleet/alerts'] = nav.alerts
    navBadges['/licenses/alerts'] = nav.alerts
  }
  if (nav.devices.count > 0) {
    navBadges['/fleet/devices'] = nav.devices
  }
  const fleetRollup = rollupBadge(nav.alerts, nav.devices)
  if (fleetRollup.count > 0) {
    navBadges['/fleet'] = fleetRollup
    navBadges['/licenses'] = fleetRollup
  }

  const isItemVisible = (item: NavItem) =>
    (!item.roles || (role && roleSatisfies(role, item.roles))) && (!item.featureFlag || features[item.featureFlag])

  const navSections = features.central ? CENTRAL_NAV_SECTIONS : EDGE_NAV_SECTIONS
  const visibleSections = navSections
    .map((section) => {
      // Dedupe same-path items after role filtering: /admin/offsite-backup
      // has a medflow_admin entry and a customer_admin entry, and role
      // inheritance would otherwise render both for medflow_admin.
      const seenPaths = new Set<string>()
      const items = section.items
        .filter(isItemVisible)
        .filter((item) => {
          if (seenPaths.has(item.path)) return false
          seenPaths.add(item.path)
          return true
        })
        .map((item) => (item.children ? { ...item, children: item.children.filter(isItemVisible) } : item))
      return { ...section, items }
    })
    .filter((section) => section.items.length > 0)

  const activeSectionKey = findActiveSectionKey(visibleSections, location.pathname)
  const { isCollapsed, toggle } = useNavCollapse(visibleSections, activeSectionKey, location.pathname)

  // Settings-hub tabs join the quick-jump index on edge deployments so
  // "print" can land directly on Settings > Printers. Filter-only: the
  // synthetic section never renders in the normal nav.
  const settingsIndexSection: NavSection | null =
    !features.central && role && roleSatisfies(role, ['admin'])
      ? {
          key: 'edge.settings-tabs',
          header: 'Settings',
          items: TAB_GROUPS.flatMap((group) =>
            group.tabs
              .filter((tab) => !tab.featureFlag || features[tab.featureFlag])
              .map((tab) => ({ path: `/settings/${tab.key}`, label: tab.label })),
          ),
        }
      : null

  const trimmedQuery = quickJumpQuery.trim()
  const isFiltering = trimmedQuery.length > 0
  const renderedSections = isFiltering
    ? filterNavSections(
        settingsIndexSection ? [...visibleSections, settingsIndexSection] : visibleSections,
        trimmedQuery,
      )
    : visibleSections

  const toggleItem = (path: string) => {
    const next = new Set(expandedItems)
    if (next.has(path)) next.delete(path)
    else next.add(path)
    setExpandedItems(next)
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <aside
        data-testid="sidebar"
        style={{
          width: '280px',
          background: colors.sidebarBg,
          color: colors.sidebarText,
          padding: 0,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${colors.sidebarBorder}`,
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: '10px 15px 9px 15px',
            borderBottom: `1px solid ${colors.sidebarBorder}`,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Link to="/">
            <img src={medflowLogo} alt="MedFlow" style={{ width: '200px' }} />
          </Link>
        </div>

        {/* Portal name */}
        <div
          style={{
            padding: '8px 15px',
            fontSize: '0.75rem',
            color: colors.textMuted,
            borderBottom: `1px solid ${colors.sidebarBorder}`,
          }}
        >
          {features.central ? 'Central Management' : 'MedFlow LIS'}
        </div>

        {/* Version, directly under the portal name. Keeps the historical
            version-footer testid from its previous life at the sidebar
            bottom. */}
        <div
          data-testid="version-footer"
          style={{
            padding: '6px 15px 8px',
            fontSize: '0.6875rem',
            color: colors.textMuted,
            borderBottom: `1px solid ${colors.sidebarBorder}`,
          }}
        >
          <div
            data-testid="version-text"
            role="button"
            tabIndex={0}
            onClick={() => setReleaseHistoryManualOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setReleaseHistoryManualOpen(true)
            }}
            title="View release history"
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = colors.accent
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = ''
            }}
          >
            Version {runtimeVersion}
          </div>
          {buildTimeVersion !== runtimeVersion && (
            <div
              data-testid="version-skew-warning"
              role="button"
              tabIndex={0}
              onClick={forceFreshReload}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') forceFreshReload()
              }}
              title={`Running ${buildTimeVersion}; a newer version (${runtimeVersion}) is available. Click to refresh.`}
              style={{
                marginTop: '4px',
                padding: '2px 6px',
                borderRadius: '3px',
                background: '#fff4d6',
                color: '#8a6d00',
                fontSize: '0.625rem',
                cursor: 'pointer',
                lineHeight: '1.3',
              }}
            >
              Click to refresh for the latest version
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ paddingTop: '6px', flex: 1, overflowY: 'auto' }}>
          <NavQuickJump query={quickJumpQuery} onChange={setQuickJumpQuery} />
          {isFiltering && renderedSections.length === 0 && (
            <div style={{ padding: '12px 15px', fontSize: '0.8125rem', color: colors.textMuted }}>No matches</div>
          )}
          {renderedSections.map((section, sectionIdx) => (
            <NavSectionBlock
              key={section.key}
              section={section}
              currentPath={location.pathname}
              navBadges={navBadges}
              collapsed={isFiltering ? false : isCollapsed(section.key)}
              onToggle={isFiltering ? undefined : () => toggle(section.key)}
              expandedItems={expandedItems}
              onToggleItem={toggleItem}
              forceExpandItems={isFiltering}
              showDivider={sectionIdx > 0}
            />
          ))}
        </nav>
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with blue top accent */}
        <div style={{ height: '2px', background: colors.accent, flexShrink: 0 }} />
        <header
          data-testid="header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 1.5rem',
            minHeight: '44px',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            background: '#fff',
            boxSizing: 'border-box',
          }}
        >
          <LayoutHeaderBrand />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ color: colors.text, fontSize: '0.8125rem' }}>
              Logged in: <strong>{user?.firstName || user?.email}</strong>
            </span>
            <NotificationBell />
            <button
              onClick={() => setSupportOpen(true)}
              data-testid="support-button"
              style={{
                padding: '0.25rem 0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.accent,
                textDecoration: 'underline',
                fontSize: '0.8125rem',
              }}
            >
              Help
            </button>
            <Link
              to="/preferences"
              data-testid="preferences-link"
              style={{
                color: colors.accent,
                textDecoration: 'underline',
                fontSize: '0.8125rem',
              }}
            >
              Preferences
            </Link>
            <button
              onClick={logout}
              data-testid="logout-button"
              style={{
                padding: '0.25rem 0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.accent,
                textDecoration: 'underline',
                fontSize: '0.8125rem',
              }}
            >
              Log out
            </button>
          </div>
        </header>

        <ImpersonationBanner />
        <UpgradeRequiredBanner />
        <main style={{ flex: 1, padding: '1rem 1.5rem', background: colors.bgPage }}>
          <Outlet />
        </main>
      </div>
      <SupportRequestModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
      <ReleaseHistoryModal
        isOpen={releaseHistoryOpen}
        onClose={closeReleaseHistory}
        currentVersion={runtimeVersion}
        previewCount={releaseHistoryManualOpen ? undefined : 3}
      />
    </div>
  )
}
